<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isCollectingSignatures } from '../composables/salesRecentNavigation';
import SalesProgressBlocks from './SalesProgressBlocks.vue';

const props = defineProps({
  dashboard: {
    type: Object,
    default: () => ({
      counts: {},
      recent: [],
      recent_list: 'rascunhos',
      recent_total: 0,
    }),
  },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['openRecent', 'selectList', 'recentListChange']);
const { t } = useI18n();
const selectedList = ref(props.dashboard.recent_list || 'rascunhos');

watch(
  () => props.dashboard.recent_list,
  value => {
    selectedList.value = value || 'rascunhos';
  }
);

const listOptions = computed(() => [
  { id: 'rascunhos', label: t('SALES.LISTS.DRAFTS') },
  { id: 'revisoes', label: t('SALES.LISTS.REVIEW') },
  { id: 'entregues', label: t('SALES.LISTS.DELIVERED') },
  { id: 'arquivados', label: t('SALES.LISTS.TRASH') },
  { id: 'finalizados', label: t('SALES.LISTS.FINISHED') },
]);

const rows = computed(() => (props.dashboard.recent || []).slice(0, 7));
const total = computed(() => props.dashboard.recent_total ?? rows.value.length);
const remaining = computed(() => Math.max(total.value - rows.value.length, 0));
const title = item => item.title || item.reference || t('SALES.COMMON.NO_DATA');
const address = item =>
  item.address?.endereco || t('SALES.COMMON.ADDRESS_UNAVAILABLE');
const statusLabel = item =>
  isCollectingSignatures(item)
    ? t('SALES.STATUS.COLLECTING_SIGNATURES')
    : item.sales_stage_label || t('SALES.STATUS.DOING');
const openItem = item => emit('openRecent', item);
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card sm:p-5"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-building-2 size-5 text-lais-primary"
          aria-hidden="true"
        />
        <h2 class="font-redHatDisplay text-lg font-semibold text-lais-text">
          {{ t('SALES.OVERVIEW.RECENT') }}
        </h2>
        <span
          v-if="total"
          class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-xs font-bold text-lais-primary"
        >
          {{ total }}
        </span>
      </div>
      <label class="flex items-center gap-2 text-xs text-lais-text-muted">
        <span>{{ t('SALES.OVERVIEW.FILTER_LABEL') }}</span>
        <select
          v-model="selectedList"
          data-testid="sales-overview-recent-filter"
          class="rounded-lais border border-lais-border-soft bg-white px-2 py-1 text-xs text-lais-text"
          :aria-label="t('SALES.OVERVIEW.FILTER_LABEL')"
          @change="emit('recentListChange', selectedList)"
        >
          <option
            v-for="option in listOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
    </div>

    <div
      v-if="loading"
      class="mt-4 h-32 animate-pulse rounded-lais bg-lais-bg-soft"
    />
    <div
      v-else-if="!rows.length"
      class="mt-4 rounded-lais border border-dashed border-lais-border-soft p-6 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.OVERVIEW.EMPTY_RECENT') }}
    </div>
    <div v-else class="mt-3 divide-y divide-lais-border-soft">
      <button
        v-for="item in rows"
        :key="item.id"
        data-testid="sales-overview-recent-row"
        type="button"
        class="w-full py-3 text-left first:pt-0 last:pb-0"
        @click="openItem(item)"
      >
        <div class="flex items-start justify-between gap-3">
          <span class="min-w-0 truncate text-sm font-semibold text-lais-text">
            {{ title(item) }}
          </span>
          <span class="shrink-0 text-xs text-lais-text-muted">
            {{ statusLabel(item) }}
          </span>
        </div>
        <span class="mt-1 block truncate text-xs text-lais-text-muted">
          {{ address(item) }}
        </span>
        <SalesProgressBlocks
          class="mt-2"
          :progress="item.progress"
          :blocks="item.block_progress"
        />
      </button>
    </div>
    <div class="mt-4 flex items-center justify-between gap-3">
      <p v-if="remaining" class="text-xs text-lais-text-muted">
        {{ t('SALES.OVERVIEW.MORE', { count: remaining }) }}
      </p>
      <span v-else />
      <button
        data-testid="sales-overview-recent-list-button"
        type="button"
        class="text-sm font-semibold text-lais-primary hover:underline"
        @click="emit('selectList', selectedList)"
      >
        {{ t('SALES.OVERVIEW.VIEW_LIST') }}
      </button>
    </div>
  </section>
</template>
